/**
 * BuildingHub — one property's command centre.
 * Route: /buildings/:propertyId (subscriber-only).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, Users, Wrench, ArrowRight, DollarSign, Loader2, Home, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";

export default function BuildingHub() {
  const { propertyId } = useParams();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  usePageTitle(hub?.building?.name ? `${hub.building.name} — Building Hub` : "Building Hub");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/landlord/buildings/${propertyId}`);
        setHub(data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 402) setError("subscribe");
        else if (status === 404) setError("notfound");
        else setError("error");
      } finally { setLoading(false); }
    })();
  }, [propertyId]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-10 flex items-center text-slate-500" data-testid="building-hub-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading building…
      </div>
    );
  }
  if (error === "subscribe") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-12" data-testid="building-hub-paywall">
        <Card className="p-8 bg-amber-50 border-amber-200">
          <div className="text-lg font-bold text-amber-900">Building Hub is a subscriber-only feature</div>
          <p className="mt-2 text-sm text-amber-800">Pick a Central Rentals plan to unlock the command centre for every property.</p>
          <Link to="/billing" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm">Open Billing <ArrowRight className="w-4 h-4"/></Link>
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-12" data-testid="building-hub-error">
        <Card className="p-8"><div className="text-sm text-slate-700">Couldn&apos;t load this building. It may have been removed.</div></Card>
      </div>
    );
  }

  const { building, units, maintenance_recent, financials, quick_actions } = hub;

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="building-hub-page">
      <div className="mb-2 text-xs">
        <Link to="/buildings" className="text-slate-500 hover:text-slate-900 underline underline-offset-4">← All buildings</Link>
      </div>
      <PageHeader title={building.name || "Unnamed building"} subtitle={building.address} icon={Building2} />

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="building-hub-stats">
        <Kpi label="Occupancy" value={`${building.occupancy_pct}%`} tone="emerald" />
        <Kpi label="Monthly rent" value={`$${(building.monthly_rent_cad || 0).toLocaleString()}`} />
        <Kpi label="Collected this month" value={`$${(financials.collected_cad || 0).toLocaleString()}`} icon={DollarSign} />
        <Kpi label="Open maintenance" value={building.open_maintenance} tone={building.open_maintenance > 0 ? "rose" : "slate"} icon={Wrench} />
      </div>

      {/* Quick actions strip */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="building-hub-actions">
        {(quick_actions || []).map((a) => (
          <Link
            key={a.href + a.label}
            to={a.href}
            data-testid={`building-hub-action-${a.label.toLowerCase().replace(/\s+/g,'-')}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${a.kind === "primary" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white border border-slate-200 hover:border-emerald-500 text-slate-800"}`}
          >
            {a.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Card className="p-5" data-testid="building-hub-units">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-slate-900">Units</h3>
              <span className="text-xs text-slate-500 tabular-nums">{units.length} total</span>
            </div>
            {units.length === 0 ? (
              <p className="text-sm text-slate-500">No units yet. Add units on the property page.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {units.map((u) => (
                  <div key={u.unit_id} data-testid={`building-hub-unit-${u.unit_id}`} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex-none flex items-center justify-center ${u.occupied ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        <Home className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 text-sm truncate">{u.label || "Unit"}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {u.occupied
                            ? `${u.tenant?.name || "Tenant"} · $${u.rent_cad?.toLocaleString?.() || 0}/mo`
                            : `Vacant · ${u.bedrooms || "—"} bed`}
                        </div>
                      </div>
                    </div>
                    <div className="flex-none flex items-center gap-2">
                      {u.is_published && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Published</span>}
                      {!u.occupied && !u.is_published && (
                        <Link
                          to={`/tenant-placement?unit=${u.unit_id}`}
                          data-testid={`building-hub-publish-${u.unit_id}`}
                          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-4"
                        >
                          Publish
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card className="p-5" data-testid="building-hub-maintenance">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-slate-900">Recent maintenance</h3>
              <Link to="/maintenance" className="text-xs font-semibold text-emerald-700 underline underline-offset-4">All jobs</Link>
            </div>
            {maintenance_recent.length === 0 ? (
              <p className="text-sm text-slate-500">No maintenance activity yet.</p>
            ) : (
              <ul className="space-y-2">
                {maintenance_recent.map((m) => (
                  <li key={m.id} className="flex items-start gap-2 text-sm">
                    <Wrench className="w-3.5 h-3.5 mt-1 flex-none text-slate-400" />
                    <div className="min-w-0">
                      <div className="text-slate-900 truncate">{m.title}</div>
                      <div className="text-xs text-slate-500 capitalize">{m.status} · {m.severity}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5 mt-5" data-testid="building-hub-financials">
            <h3 className="font-display text-lg font-bold text-slate-900 mb-2">This month</h3>
            <Row label="Expected rent" value={`$${(financials.expected_cad || 0).toLocaleString()}`} />
            <Row label="Collected" value={`$${(financials.collected_cad || 0).toLocaleString()}`} />
            <Row label="Collection rate" value={`${financials.collection_rate_pct}%`} last />
            <Link to="/payments" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 underline underline-offset-4">
              Full rent ledger <ExternalLink className="w-3 h-3" />
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "slate", icon: Icon }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-100",
    rose: "bg-rose-50 text-rose-900 border-rose-100",
    slate: "bg-white text-slate-900 border-slate-200",
  }[tone] || "bg-white text-slate-900 border-slate-200";
  return (
    <div className={`rounded-xl px-4 py-3 border ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="mt-1 text-xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div className={`flex items-center justify-between py-2 text-sm ${last ? "" : "border-b border-slate-100"}`}>
      <span className="text-slate-600">{label}</span>
      <span className="font-bold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
